import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */
/** @typedef {{ key: string, quantity: number, variant_id?: number, properties?: Record<string, unknown> }} CartItem */

const EMBOSSING_PROPERTY_NAME = 'Embossing';
const EMBOSSING_FEE_PROPERTY_KEY = '_fee';
const EMBOSSING_FEE_PROPERTY_VALUE = 'embossing';
const EMBOSSING_FEE_VARIANT_ID = document.querySelector(
    '[data-embossing-select] option[data-embossing-option]',
  )?.dataset?.embossingOption || 47898898596151;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.#updateLineItemQuantityAndEmbossingFee(line, quantity);
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#updateLineItemAndEmbossingFee(line);

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Removes a line item and decrements the embossing fee quantity when needed.
   * @param {number} line - The line item index.
   */
  async #updateLineItemAndEmbossingFee(line) {
    const updatePayload = await this.#getEmbossingSyncedPayload(line, 0);

    if (updatePayload?.updates) {
      this.updateQuantities({
        updates: updatePayload.updates,
        action: 'clear',
        line,
      });
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates a line item and keeps the embossing fee in sync when needed.
   * @param {number} line - The line item index.
   * @param {number} quantity - The new quantity.
   */
  async #updateLineItemQuantityAndEmbossingFee(line, quantity) {
    const updatePayload = await this.#getEmbossingSyncedPayload(line, quantity);

    if (updatePayload?.updates) {
      this.updateQuantities({
        updates: updatePayload.updates,
        action: 'change',
        line,
      });
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Builds cart updates for a line quantity change and the matching embossing fee line.
   * @param {number} line - The line item index.
   * @param {number} nextQuantity - The requested quantity for the line.
   * @returns {Promise<{updates: Record<string, number>} | null>}
   */
  async #getEmbossingSyncedPayload(line, nextQuantity) {
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      if (!cartResponse.ok) return null;

      const cart = /** @type {{ items?: CartItem[] }} */ (await cartResponse.json());
      const cartItems = cart.items || [];
      const lineItem = cartItems[line - 1];
      if (!lineItem) return null;

      const currentLineQuantity = Number(lineItem.quantity) || 0;
      const requestedLineQuantity = Math.max(0, Number(nextQuantity) || 0);

      const embossingValue = String(lineItem.properties?.[EMBOSSING_PROPERTY_NAME] || '').trim();
      if (!embossingValue) return null;

      const embossingFeeItem = cartItems.find(
        (item) => item.key !== lineItem.key && this.#isEmbossingFeeItem(item)
      );
      if (!embossingFeeItem) return null;

      const currentFeeQuantity = Number(embossingFeeItem.quantity) || 0;
      const lineQuantityDelta = requestedLineQuantity - currentLineQuantity;
      const nextFeeQuantity = Math.max(0, currentFeeQuantity + lineQuantityDelta);

      return {
        updates: {
          [lineItem.key]: requestedLineQuantity,
          [embossingFeeItem.key]: nextFeeQuantity,
        },
      };
    } catch (error) {
      console.warn('Failed to prepare embossing fee synchronized update', error);
      return null;
    }
  }

  /**
   * @param {{ variant_id?: number, properties?: Record<string, unknown> }} item
   * @returns {boolean}
   */
  #isEmbossingFeeItem(item) {
    const feePropertyValue = item.properties?.[EMBOSSING_FEE_PROPERTY_KEY];
    const isEmbossingFeeProperty =
      typeof feePropertyValue === 'string' && feePropertyValue.toLowerCase() === EMBOSSING_FEE_PROPERTY_VALUE;

    return isEmbossingFeeProperty || Number(item.variant_id) === EMBOSSING_FEE_VARIANT_ID;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { line, quantity, action } = config;

    this.#performCartUpdate({
      body: {
        line,
        quantity,
      },
      action,
      line,
    });
  }

  /**
   * Updates multiple line item quantities by key.
   * @param {Object} config - The config.
   * @param {Record<string, number>} config.updates - A map of line item keys to quantities.
   * @param {string} config.action - The action.
   * @param {number} [config.line] - The user-triggered line index for error reporting.
   */
  updateQuantities(config) {
    this.#performCartUpdate({
      body: {
        updates: config.updates,
      },
      action: config.action,
      line: config.line,
      endpoint: Theme.routes.cart_update_url || '/cart/update.js',
    });
  }

  /**
   * Performs a cart update request and refreshes cart sections.
   * @param {Object} config - The config.
   * @param {Record<string, unknown>} config.body - The cart API body payload.
   * @param {string} config.action - The action name for performance tracking.
   * @param {number} [config.line] - The user-triggered line index for error reporting.
   * @param {string} [config.endpoint] - The cart API endpoint.
   */
  #performCartUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      ...config.body,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(config.endpoint || Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          if (config.line) {
            this.#handleCartError(config.line, parsedResponseText);
          }
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Gets all cart section IDs that should be refreshed after cart mutations.
   * @returns {Set<string>} Section IDs.
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
